import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { FlaskConical, BookOpen, FileText, Star, ChevronRight, RefreshCw, CheckCircle, Clock, AlertCircle, ExternalLink, Columns, X, Mic, MicOff, Volume2, Globe } from 'lucide-react';
import { FileAttachments } from '../components/FileAttachments';
import DiscoveryChat from '../components/DiscoveryChat';
import ModelSelector from '../components/ModelSelector';

const API = process.env.REACT_APP_BACKEND_URL;

const PHASE_COLORS = {
  A: { bg: 'rgba(124,94,250,0.1)', border: 'rgba(124,94,250,0.3)', text: '#a68eff' },
  B: { bg: 'rgba(0,212,184,0.1)', border: 'rgba(0,212,184,0.3)', text: '#00d4b8' },
  C: { bg: 'rgba(255,83,112,0.1)', border: 'rgba(255,83,112,0.3)', text: '#ff7a94' },
  D: { bg: 'rgba(240,194,127,0.1)', border: 'rgba(240,194,127,0.3)', text: '#f0c27f' },
  E: { bg: 'rgba(61,255,160,0.1)', border: 'rgba(61,255,160,0.3)', text: '#3dffa0' },
  F: { bg: 'rgba(96,165,250,0.1)', border: 'rgba(96,165,250,0.3)', text: '#60a5fa' },
  G: { bg: 'rgba(249,115,22,0.1)', border: 'rgba(249,115,22,0.3)', text: '#fb923c' },
  H: { bg: 'rgba(167,139,250,0.1)', border: 'rgba(167,139,250,0.3)', text: '#c4b5fd' },
};

const SOURCE_BADGE = { semantic_scholar: '#3dffa0', arxiv: '#60a5fa' };

const RESEARCH_KEY = 'research_sessions';
function saveResearchSession(session) {
  try {
    const stored = JSON.parse(localStorage.getItem(RESEARCH_KEY) || '[]');
    stored.unshift(session);
    localStorage.setItem(RESEARCH_KEY, JSON.stringify(stored.slice(0, 5)));
  } catch {}
}
function loadResearchSessions() {
  try { return JSON.parse(localStorage.getItem(RESEARCH_KEY) || '[]'); } catch { return []; }
}

function ResearchComparePanel({ sessions, onClose }) {
  const [a, b] = sessions.slice(0, 2);
  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: 'rgba(5,5,15,0.97)', backdropFilter: 'blur(20px)' }}>
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid var(--llmc-border)' }}>
        <div className="flex items-center gap-2">
          <Columns size={16} style={{ color: '#3dffa0' }} />
          <span className="font-black text-white text-sm">Research Comparison</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(61,255,160,0.12)', color: '#3dffa0' }}>Last 2 sessions</span>
        </div>
        <button onClick={onClose} className="p-2 rounded-lg hover:opacity-80" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--llmc-text-muted)' }}>
          <X size={16} />
        </button>
      </div>
      <div className="flex-1 overflow-hidden flex">
        {[a, b].filter(Boolean).map((sess, i) => (
          <div key={i} className="flex-1 overflow-y-auto p-5 border-r" style={{ borderColor: 'var(--llmc-border)' }}>
            <div className="mb-4 pb-3" style={{ borderBottom: '1px solid var(--llmc-border)' }}>
              <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: i === 0 ? '#3dffa0' : '#60a5fa' }}>
                {i === 0 ? 'Research A (Latest)' : 'Research B (Previous)'}
              </div>
              <div className="text-sm font-semibold text-white mb-0.5 line-clamp-2">{sess.topic}</div>
              <div className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>
                {new Date(sess.timestamp).toLocaleDateString()}
              </div>
            </div>
            <div className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: 'rgba(255,255,255,0.75)' }}>
              {sess.blueprint || 'No blueprint available'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PhaseRow({ phase }) {
  const [expanded, setExpanded] = useState(false);
  const colors = PHASE_COLORS[phase.phase] || PHASE_COLORS.A;
  const isRunning = phase.status === 'running';
  const isDone = phase.status === 'done';
  const isError = phase.status === 'error';

  return (
    <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${isDone ? colors.border : 'var(--llmc-border)'}`, background: isDone ? colors.bg : 'rgba(255,255,255,0.02)' }}>
      <div className="flex items-center gap-3 px-4 py-2.5 cursor-pointer" onClick={() => isDone && setExpanded(e => !e)}>
        <div className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-black flex-shrink-0"
          style={{ background: isDone ? colors.text + '22' : 'rgba(255,255,255,0.06)', color: isDone ? colors.text : 'var(--llmc-text-muted)' }}>
          {isDone ? <CheckCircle size={12} /> : isRunning ? (
            <div className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: colors.text }} />
          ) : isError ? <AlertCircle size={12} className="text-red-400" /> : phase.phase}
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-xs font-semibold text-white">{phase.phase}. {phase.label}</span>
          {phase.output && isDone && (
            <span className="ml-2 text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>
              — {phase.output.slice(0, 60)}{phase.output.length > 60 ? '…' : ''}
            </span>
          )}
        </div>
        {isRunning && <span className="text-[9px] font-bold animate-pulse" style={{ color: colors.text }}>RUNNING</span>}
        {isDone && phase.output && (
          <ChevronRight size={12} className="transition-transform flex-shrink-0"
            style={{ color: 'var(--llmc-text-muted)', transform: expanded ? 'rotate(90deg)' : 'rotate(0)' }} />
        )}
      </div>
      {expanded && isDone && phase.output && (
        <div className="px-4 pb-3">
          <div className="text-xs leading-relaxed whitespace-pre-wrap rounded-lg p-3"
            style={{ background: 'rgba(0,0,0,0.3)', color: 'var(--llmc-text-muted)', maxHeight: 300, overflowY: 'auto' }}>
            {phase.output}
          </div>
        </div>
      )}
    </div>
  );
}

function PaperCard({ paper }) {
  return (
    <div className="rounded-xl p-3 flex gap-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--llmc-border)' }}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded"
            style={{ background: `${SOURCE_BADGE[paper.source] || '#7c5efa'}22`, color: SOURCE_BADGE[paper.source] || '#7c5efa' }}>
            {paper.source === 'semantic_scholar' ? 'S2' : 'arXiv'}
          </span>
          {paper.year > 0 && <span className="text-[9px]" style={{ color: 'var(--llmc-text-muted)' }}>{paper.year}</span>}
          {paper.relevanceScore != null && (
            <span className="text-[9px] font-bold" style={{ color: '#f0c27f' }}>{paper.relevanceScore}% relevant</span>
          )}
        </div>
        <div className="text-xs font-semibold text-white mb-1 line-clamp-2">{paper.title}</div>
        {paper.abstract && (
          <div className="text-[10px] line-clamp-2" style={{ color: 'var(--llmc-text-muted)' }}>{paper.abstract}</div>
        )}
      </div>
      {paper.url && (
        <a href={paper.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 hover:opacity-70">
          <ExternalLink size={12} style={{ color: 'var(--llmc-text-muted)' }} />
        </a>
      )}
    </div>
  );
}

function ReviewCard({ review }) {
  const score = review.score || 0;
  const color = score >= 8 ? '#3dffa0' : score >= 6.5 ? '#f0c27f' : '#ff5370';
  return (
    <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--llmc-border)' }}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-bold text-white">{review.model}</div>
        <div className="text-sm font-black" style={{ color }}>{score}/10</div>
      </div>
      {review.strengths?.length > 0 && (
        <div className="text-[10px] mb-1" style={{ color: '#3dffa0' }}>+ {review.strengths.join(' · ')}</div>
      )}
      {review.weaknesses?.length > 0 && (
        <div className="text-[10px] mb-1" style={{ color: '#ff7a94' }}>- {review.weaknesses.join(' · ')}</div>
      )}
      {review.suggestion && (
        <div className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>Suggestion: {review.suggestion}</div>
      )}
    </div>
  );
}

export default function ResearchLab() {
  const [topic, setTopic] = useState(() => {
    const prefill = localStorage.getItem('ideaworks_prefill_idea');
    if (prefill) { localStorage.removeItem('ideaworks_prefill_idea'); return prefill; }
    return '';
  });
  const [sessionId, setSessionId] = useState(null);
  const [phases, setPhases] = useState([]);
  const [literature, setLiterature] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [finalBlueprint, setFinalBlueprint] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('phases');
  const [attachments, setAttachments] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [sessionHistory, setSessionHistory] = useState(() => loadResearchSessions());
  const [clarify, setClarify] = useState({ loading: false, shown: false, q1: '', q2: '', a1: '', a2: '' });
  const [discoveryContext, setDiscoveryContext] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [judgeModel, setJudgeModel] = useState(null); // null = default (Claude Sonnet 4.6)
  const esRef = useRef(null);
  const recognitionRef = useRef(null);
  const bottomRef = useRef(null);
  const location = useLocation();

  // Pre-fill topic from Brainstorm Lab or Trending Topics navigation
  useEffect(() => {
    if (location.state?.topic) {
      setTopic(location.state.topic.slice(0, 600));
    }
  }, [location.state]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [phases, status]);

  const getClarifyQuestions = async () => {
    if (!topic.trim()) return;
    setClarify(c => ({ ...c, loading: true }));
    try {
      const { data } = await axios.post(`${API}/api/research/clarify`, { topic });
      setClarify(c => ({ ...c, loading: false, shown: true, q1: data.q1, q2: data.q2, a1: '', a2: '' }));
    } catch {
      setClarify(c => ({ ...c, loading: false, shown: true, q1: "What specific industry or domain should the research focus on?", q2: "What is the single most important question you need answered?", a1: '', a2: '' }));
    }
  };

  const toggleVoiceInput = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Voice recognition not supported in this browser'); return; }
    if (isListening) { recognitionRef.current?.stop(); setIsListening(false); return; }
    const r = new SR();
    r.continuous = false; r.interimResults = false; r.lang = 'en-US';
    r.onresult = e => { setTopic(e.results[0][0].transcript); setIsListening(false); };
    r.onend = () => setIsListening(false);
    r.onerror = () => setIsListening(false);
    recognitionRef.current = r; r.start(); setIsListening(true);
  }, [isListening]);

  const speakText = useCallback(async (text) => {
    if (!ttsEnabled) return;
    try {
      const { data } = await axios.post(`${API}/api/voice/tts`, { text: text.slice(0, 400), voice: 'am_adam' });
      if (data.audio_base64) {
        const bytes = atob(data.audio_base64); const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        const audio = new Audio(URL.createObjectURL(new Blob([arr], { type: 'audio/wav' }))); audio.play();
      }
    } catch {}
  }, [ttsEnabled]);

  const startResearch = useCallback(async (contextToUse) => {
    const theTopic = contextToUse || discoveryContext || topic;
    if (!theTopic.trim() || status === 'running') return;
    setError(null);
    setPhases([]);
    setLiterature([]);
    setReviews([]);
    setFinalBlueprint('');
    setStatus('connecting');
    setActiveTab('phases');

    try {
      const { data } = await axios.post(`${API}/api/research/start`, {
        topic: theTopic,
        attachments: attachments.map(a => ({ name: a.name, type: a.type, content: a.content })),
        ...(judgeModel ? { judge_model: judgeModel } : {}),
      });
      const sid = data.sessionId;
      setSessionId(sid);
      setStatus('running');

      const es = new EventSource(`${API}/api/research/stream/${sid}`);
      esRef.current = es;

      es.addEventListener('init', e => {
        const d = JSON.parse(e.data);
        if (d.session?.phases) {
          setPhases(d.session.phases.map(p => ({ ...p, status: 'pending', output: null })));
        }
      });

      es.addEventListener('phase', e => {
        const d = JSON.parse(e.data);
        setPhases(prev => {
          const existing = prev.find(p => p.phase === d.phase);
          if (existing) {
            return prev.map(p => p.phase === d.phase ? { ...p, ...d } : p);
          }
          return [...prev, d];
        });
      });

      es.addEventListener('literature', e => {
        const d = JSON.parse(e.data);
        if (d.papers) setLiterature(d.papers);
      });

      es.addEventListener('reviews', e => {
        const d = JSON.parse(e.data);
        if (d.reviews) setReviews(d.reviews);
      });

      es.addEventListener('complete', e => {
        setStatus('complete');
        // Fetch final session to get blueprint
        axios.get(`${API}/api/research/session/${sid}`).then(res => {
          const s = res.data?.session;
          if (s?.finalBlueprint) {
            setFinalBlueprint(s.finalBlueprint);
            // Save to comparison history
            const sess = { id: sid, topic, blueprint: s.finalBlueprint, timestamp: Date.now() };
            saveResearchSession(sess);
            setSessionHistory(loadResearchSessions());
            // TTS read-back of top 3 findings if enabled
            speakText(`Research complete. Here are the key findings: ${s.finalBlueprint.slice(0, 350)}`);
          }
        }).catch(() => {});
        es.close();
      });

      es.addEventListener('error', e => {
        if (!e.data) return; // Native SSE connection event — ignore
        try {
          const d = JSON.parse(e.data);
          setError(d.error || 'Research failed');
        } catch {
          setError('Stream error');
        }
        setStatus('error');
        es.close();
      });

      es.onerror = () => {
        if (status === 'running') setStatus('complete');
        es.close();
      };

    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to start research');
      setStatus('error');
    }
  }, [topic, status, discoveryContext, attachments]);

  const reset = () => {
    if (esRef.current) esRef.current.close();
    setSessionId(null);
    setPhases([]);
    setLiterature([]);
    setReviews([]);
    setFinalBlueprint('');
    setStatus('idle');
    setError(null);
    setTopic('');
    setAttachments([]);
    setDiscoveryContext(null);
    setClarify({ loading: false, shown: false, q1: '', q2: '', a1: '', a2: '' });
  };

  const EXAMPLES = [
    'Large Language Models in cybersecurity threat detection 2025',
    'Impact of AI on content marketing and SEO strategies',
    'Zero-trust architecture for remote-first enterprises',
    'Retrieval augmented generation for enterprise knowledge bases',
  ];

  const tabs = [
    { id: 'phases', label: 'Research Phases', count: phases.filter(p => p.status === 'done').length + '/' + phases.length },
    { id: 'literature', label: 'Papers Found', count: literature.length },
    { id: 'reviews', label: 'Peer Reviews', count: reviews.length },
    { id: 'blueprint', label: 'Final Blueprint', count: finalBlueprint ? '✓' : '' },
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ background: 'var(--llmc-bg)' }}>
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4" style={{ borderBottom: '1px solid var(--llmc-border)', background: 'rgba(10,10,20,0.95)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #3dffa0, #00d4b8)' }}>
              <FlaskConical size={20} className="text-white" />
            </div>
            <div>
              <div className="font-black text-white text-sm" style={{ letterSpacing: '-0.01em' }}>Research Lab</div>
              <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>8-Phase Autonomous Research — Semantic Scholar · arXiv · Peer Review · Quality Gate</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-[10px] px-2.5 py-1 rounded-full font-bold"
              style={{ background: 'rgba(61,255,160,0.08)', border: '1px solid rgba(61,255,160,0.2)', color: '#3dffa0' }}>
              Claude Sonnet 4.6 Judge
            </div>
            {status !== 'idle' && (
              <button onClick={reset} data-testid="research-reset-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--llmc-border)', color: 'var(--llmc-text-muted)' }}>
                <RefreshCw size={12} /> Reset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {(status === 'idle' || status === 'discovery') && (
          <div className="max-w-2xl mx-auto space-y-4" style={{ animation: 'fade-in 0.4s ease' }}>
            <div className="text-center space-y-2 py-8">
              <div className="text-3xl font-black text-white" style={{ letterSpacing: '-0.02em' }}>Deep Research on Any Topic</div>
              <div className="text-sm" style={{ color: 'var(--llmc-text-muted)' }}>8 autonomous phases: literature discovery → synthesis → draft → peer review → quality gate → final blueprint.</div>
            </div>
            <div className="rounded-2xl p-5" style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
              <textarea
                value={topic}
                onChange={e => setTopic(e.target.value)}
                data-testid="research-topic-input"
                placeholder="Enter a research topic, question, or domain to investigate..."
                className="w-full bg-transparent resize-none text-sm outline-none leading-relaxed"
                style={{ color: 'var(--llmc-text)', minHeight: 100 }}
                onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { if (topic.trim()) setStatus('discovery'); } }}
              />
              {(location.state?.fromBrainstorm || location.state?.fromTrending) && (
                <div className="flex items-center gap-2 mt-2 mb-2 px-2 py-1.5 rounded-lg text-[10px]"
                  style={{ background: 'rgba(61,255,160,0.08)', border: '1px solid rgba(61,255,160,0.2)', color: '#3dffa0' }}>
                  <FlaskConical size={10} />
                  {location.state?.fromTrending ? 'Topic from Trending Topics — edit as needed' : 'Pre-filled from Brainstorm Lab — edit as needed'}
                </div>
              )}

              {/* Discovery Mode panel */}
              {status === 'discovery' && topic.trim() && (
                <div className="mt-3">
                  <DiscoveryChat
                    context={topic}
                    sessionType="research"
                    accentColor="#3dffa0"
                    accentBg="rgba(61,255,160,0.06)"
                    onComplete={({ enrichedContext }) => {
                      setDiscoveryContext(enrichedContext);
                      startResearch(enrichedContext);
                    }}
                    onSkip={(ctx) => {
                      setStatus('idle');
                      startResearch(ctx);
                    }}
                  />
                </div>
              )}

              {status === 'idle' && (
              <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: '1px solid var(--llmc-border)' }}>
                <div className="flex items-center gap-2">
                  <FileAttachments attachments={attachments} onChange={setAttachments} />
                  {/* Voice input button */}
                  <button onClick={toggleVoiceInput} data-testid="research-voice-btn"
                    title={isListening ? 'Stop listening' : 'Speak your research topic'}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
                    style={{ background: isListening ? 'rgba(255,80,80,0.15)' : 'rgba(255,255,255,0.05)', border: `1px solid ${isListening ? 'rgba(255,80,80,0.4)' : 'rgba(255,255,255,0.1)'}`, color: isListening ? '#ff5050' : 'rgba(255,255,255,0.4)' }}>
                    {isListening ? <><MicOff size={12} className="animate-pulse" /> Listening...</> : <><Mic size={12} /> Voice</>}
                  </button>
                  {/* TTS toggle */}
                  <button onClick={() => setTtsEnabled(v => !v)} data-testid="research-tts-btn"
                    title={ttsEnabled ? 'Disable Einstein read-back' : 'Enable Einstein read-back of findings'}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
                    style={{ background: ttsEnabled ? 'rgba(61,255,160,0.1)' : 'rgba(255,255,255,0.05)', border: `1px solid ${ttsEnabled ? 'rgba(61,255,160,0.3)' : 'rgba(255,255,255,0.1)'}`, color: ttsEnabled ? '#3dffa0' : 'rgba(255,255,255,0.4)' }}>
                    <Volume2 size={12} /> {ttsEnabled ? 'Read-back On' : 'Read-back'}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  {sessionHistory.length >= 2 && (
                    <button
                      onClick={() => setShowCompare(true)}
                      data-testid="research-compare-btn"
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
                      style={{ background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.3)', color: '#60a5fa' }}
                    >
                      <Columns size={12} /> Compare
                    </button>
                  )}
                  <button onClick={() => { if (topic.trim()) setStatus('discovery'); }} disabled={!topic.trim()} data-testid="research-start-btn"
                    className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold transition-all hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: 'linear-gradient(135deg, #3dffa0, #00d4b8)', color: '#0a0a0f' }}>
                    <FlaskConical size={15} /> Start Discovery
                  </button>
                  <ModelSelector lab="research" onChange={setJudgeModel} />
                </div>
              </div>
              )}
              {status === 'discovery' && (
                <button onClick={() => setStatus('idle')} className="mt-2 text-[10px] hover:opacity-60 transition-opacity" style={{ color: 'var(--llmc-text-muted)' }}>
                  ← Back to edit topic
                </button>
              )}
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--llmc-text-muted)' }}>Example topics</div>
              <div className="grid grid-cols-2 gap-2">
                {EXAMPLES.map((ex, i) => (
                  <button key={i} onClick={() => setTopic(ex)} data-testid={`research-example-${i}`}
                    className="text-left px-3 py-2.5 rounded-xl text-xs transition-all hover:opacity-80"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--llmc-border)', color: 'var(--llmc-text-muted)' }}>
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {status !== 'idle' && status !== 'discovery' && (
          <div className="max-w-2xl mx-auto space-y-4">
            {/* Topic */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(61,255,160,0.06)', border: '1px solid rgba(61,255,160,0.2)' }}>
              <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'rgba(61,255,160,0.7)' }}>Researching</div>
              <div className="text-sm font-semibold text-white">{topic}</div>
            </div>

            {/* Status */}
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl"
              style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
              {status === 'running' ? <>
                <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#3dffa0' }} />
                <span className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
                  Pipeline running — {phases.filter(p => p.status === 'done').length}/{phases.length} phases complete
                </span>
              </> : status === 'complete' ? <>
                <CheckCircle size={14} style={{ color: '#3dffa0' }} />
                <span className="text-xs font-medium" style={{ color: '#3dffa0' }}>Research complete — blueprint ready</span>
              </> : <>
                <AlertCircle size={14} style={{ color: '#ff5370' }} />
                <span className="text-xs" style={{ color: '#ff5370' }}>{error || 'Research failed'}</span>
              </>}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 rounded-xl p-1" style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
              {tabs.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`research-tab-${tab.id}`}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-semibold transition-all"
                  style={{
                    background: activeTab === tab.id ? 'rgba(61,255,160,0.1)' : 'transparent',
                    color: activeTab === tab.id ? '#3dffa0' : 'var(--llmc-text-muted)',
                    border: activeTab === tab.id ? '1px solid rgba(61,255,160,0.3)' : '1px solid transparent',
                  }}>
                  {tab.label}
                  {tab.count ? <span className="opacity-60">({tab.count})</span> : null}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'phases' && (
              <div className="space-y-2">
                {phases.map(phase => <PhaseRow key={phase.phase} phase={phase} />)}
                {phases.length === 0 && status === 'running' && (
                  <div className="text-center py-8 text-sm" style={{ color: 'var(--llmc-text-muted)' }}>
                    Initializing pipeline...
                  </div>
                )}
              </div>
            )}

            {activeTab === 'literature' && (
              <div className="space-y-2">
                {literature.length === 0 ? (
                  <div className="text-center py-8 text-sm" style={{ color: 'var(--llmc-text-muted)' }}>
                    {status === 'running' ? 'Discovering literature...' : 'No papers found'}
                  </div>
                ) : literature.map((paper, i) => <PaperCard key={i} paper={paper} />)}
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="space-y-2">
                {reviews.length === 0 ? (
                  <div className="text-center py-8 text-sm" style={{ color: 'var(--llmc-text-muted)' }}>
                    {status === 'running' ? 'Peer review in progress...' : 'No reviews yet'}
                  </div>
                ) : (
                  <>
                    <div className="text-xs text-center py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', color: 'var(--llmc-text-muted)' }}>
                      Average Score: {reviews.length > 0 ? (reviews.reduce((s, r) => s + (r.score || 0), 0) / reviews.length).toFixed(1) : '—'}/10
                      {reviews.length > 0 && (reviews.reduce((s, r) => s + (r.score || 0), 0) / reviews.length) < 6.5
                        ? ' → Revision triggered'
                        : ' → Quality gate passed'}
                    </div>
                    {reviews.map((r, i) => <ReviewCard key={i} review={r} />)}
                  </>
                )}
              </div>
            )}

            {activeTab === 'blueprint' && (
              <div>
                {finalBlueprint ? (
                  <div className="rounded-xl p-5 text-sm leading-relaxed whitespace-pre-wrap"
                    style={{ background: 'rgba(61,255,160,0.04)', border: '1px solid rgba(61,255,160,0.2)', color: '#e8e8f0' }}>
                    {finalBlueprint}
                  </div>
                ) : (
                  <div className="text-center py-12 text-sm" style={{ color: 'var(--llmc-text-muted)' }}>
                    {status === 'running' ? 'Blueprint is being generated...' : 'No blueprint available yet'}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Compare Panel */}
      {showCompare && <ResearchComparePanel sessions={sessionHistory} onClose={() => setShowCompare(false)} />}
    </div>
  );
}
